import { useEffect, useState } from "react";
import { http, apiError } from "@/lib/api";
import { CheckSquare, Square, Flame, Star, Moon, CheckCircle, MinusCircle, X } from "@phosphor-icons/react";

export default function HabitCheckin() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [pending, setPending] = useState({}); // habit_id -> bool while in-flight
  const [sleepInput, setSleepInput] = useState("");
  const [sleepSaving, setSleepSaving] = useState(false);
  // Tapping a past-day bar opens a modal that reveals per-habit ticks + sleep
  // for that specific day (drills into the 14-day mini-grid).
  const [drillDay, setDrillDay] = useState(null);

  const load = async () => {
    try {
      const { data } = await http.get("/client/habits");
      setData(data);
      if (typeof data?.sleep_hours_today === "number") {
        setSleepInput(String(data.sleep_hours_today));
      }
    } catch (e) { setErr(apiError(e)); }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (habit) => {
    if (pending[habit.id]) return;
    // Sleep habit doesn't toggle by click — must be set via the hours input.
    if (habit.id === data?.sleep_habit_id) return;
    const next = !data?.completed?.[habit.id];
    setPending((p) => ({ ...p, [habit.id]: true }));
    // optimistic update
    setData((cur) => cur ? { ...cur, completed: { ...cur.completed, [habit.id]: next } } : cur);
    try {
      const { data: fresh } = await http.post("/client/habits", { habit_id: habit.id, completed: next });
      setData(fresh);
    } catch (e) {
      setErr(apiError(e));
      // rollback
      setData((cur) => cur ? { ...cur, completed: { ...cur.completed, [habit.id]: !next } } : cur);
    } finally {
      setPending((p) => { const np = { ...p }; delete np[habit.id]; return np; });
    }
  };

  const saveSleep = async () => {
    const hours = parseFloat(sleepInput);
    if (Number.isNaN(hours) || hours < 0 || hours > 24) {
      setErr("Enter a sleep value between 0 and 24 hours.");
      return;
    }
    setSleepSaving(true);
    setErr("");
    try {
      const { data: fresh } = await http.post("/client/sleep", { hours });
      setData(fresh);
      setSleepInput(String(hours));
    } catch (e) {
      setErr(apiError(e));
    } finally {
      setSleepSaving(false);
    }
  };

  if (!data) return null;
  const habits = data.habits || [];
  const sleepHabitId = data.sleep_habit_id;
  const sleepMin = data.sleep_minimum_hours || 6;

  if (habits.length === 0) {
    return (
      <div className="id-card" data-testid="habits-empty">
        <div className="id-label mb-2 text-[#FF3333]">★ Daily Habits</div>
        <p className="text-zinc-500 text-sm font-manrope">
          Your coach hasn&apos;t set your daily habits yet. They&apos;ll show up here as a daily check-in.
        </p>
      </div>
    );
  }

  const doneToday = habits.reduce((acc, h) => acc + (data.completed?.[h.id] ? 1 : 0), 0);
  const pct = Math.round((doneToday / habits.length) * 100);

  return (
    <div className="id-card" data-testid="habits-card">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="id-label mb-1 text-[#FF3333]">★ Today&apos;s Habits</div>
          <div className="font-oswald text-2xl uppercase tracking-wide">{doneToday} / {habits.length} done</div>
          <div className="text-xs text-zinc-500 font-manrope mt-1">{pct}% complete · {data.date}</div>
        </div>
        <div className="text-right">
          <div className="id-label text-zinc-500 mb-1 flex items-center gap-1 justify-end">
            <Flame size={12} weight="fill" /> Streak
          </div>
          <div className="font-oswald text-3xl text-white" data-testid="habits-streak">{data.streak}</div>
          <div className="text-[10px] text-zinc-500 uppercase tracking-[0.2em] font-bold mt-1">day{data.streak === 1 ? "" : "s"}</div>
        </div>
      </div>

      <ul className="space-y-2" data-testid="habits-list">
        {habits.map((h) => {
          const done = !!data.completed?.[h.id];
          const busy = !!pending[h.id];
          const isSleep = h.id === sleepHabitId;
          if (isSleep) {
            return (
              <li key={h.id}>
                <div
                  data-testid={`habit-sleep-${h.id}`}
                  className={`w-full flex items-center gap-3 px-4 py-3 border transition-colors duration-150 ${
                    done
                      ? "border-[#FF3333] bg-[#FF3333]/10 text-white"
                      : "border-[#262626] bg-[#0F0F0F] text-zinc-300"
                  }`}
                >
                  {done
                    ? <CheckSquare size={20} weight="fill" className="text-[#FF3333] flex-shrink-0" />
                    : <Moon size={20} weight="fill" className="text-indigo-400 flex-shrink-0" />}
                  <span className="font-manrope text-sm flex-1">{h.label}</span>
                  <div className="flex items-center gap-1.5">
                    <input
                      type="number"
                      min="0"
                      max="24"
                      step="0.25"
                      value={sleepInput}
                      onChange={(e) => setSleepInput(e.target.value)}
                      placeholder={String(sleepMin)}
                      className="w-16 px-2 py-1 bg-[#0A0A0A] border border-[#262626] text-white font-manrope text-sm text-center focus:border-[#FF3333] outline-none"
                      data-testid="habit-sleep-hours-input"
                    />
                    <span className="text-zinc-500 text-xs font-manrope">h</span>
                    <button
                      type="button"
                      onClick={saveSleep}
                      disabled={sleepSaving || sleepInput === ""}
                      className="px-3 py-1 border border-[#FF3333] text-[#FF3333] hover:bg-[#FF3333] hover:text-white transition-colors uppercase font-oswald text-[10px] tracking-[0.15em] font-bold disabled:opacity-40"
                      data-testid="habit-sleep-save"
                    >
                      {sleepSaving ? "..." : "Log"}
                    </button>
                  </div>
                </div>
              </li>
            );
          }
          return (
            <li key={h.id}>
              <button
                onClick={() => toggle(h)}
                disabled={busy}
                data-testid={`habit-toggle-${h.id}`}
                className={`w-full flex items-center gap-3 px-4 py-3 border transition-colors duration-150 text-left ${
                  done
                    ? "border-[#FF3333] bg-[#FF3333]/10 text-white"
                    : "border-[#262626] bg-[#0F0F0F] text-zinc-300 hover:border-zinc-500"
                }`}
              >
                {done
                  ? <CheckSquare size={20} weight="fill" className="text-[#FF3333] flex-shrink-0" />
                  : <Square size={20} weight="bold" className="text-zinc-500 flex-shrink-0" />}
                <span className="font-manrope text-sm">{h.label}</span>
              </button>
            </li>
          );
        })}
      </ul>

      {/* Last 14 days mini-grid — tap any bar to drill into that day */}
      <div className="mt-5 pt-4 border-t border-[#262626]">
        <div className="id-label mb-2 flex items-center gap-1"><Star size={11} weight="fill" /> Last 14 days · tap a bar</div>
        <div className="flex gap-1" data-testid="habits-history">
          {(data.last14 || []).map((d) => {
            const pct2 = d.total ? d.done / d.total : 0;
            const color = pct2 === 1 ? "bg-[#FF3333]" : pct2 > 0 ? "bg-[#FF3333]/40" : "bg-[#1A1A1A] border border-[#262626]";
            return (
              <button
                key={d.date}
                onClick={() => setDrillDay(d)}
                className={`flex-1 h-6 ${color} hover:ring-1 hover:ring-white transition-all`}
                title={`${d.date} · ${d.done}/${d.total}`}
                data-testid={`habit-day-${d.date}`}
                aria-label={`View ${d.date} details`}
              />
            );
          })}
        </div>
      </div>

      {/* Per-day drill-in modal */}
      {drillDay && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4"
          onClick={() => setDrillDay(null)}
          data-testid="habit-day-drill"
        >
          <div
            className="bg-[#0A0A0A] border border-[#262626] w-full sm:max-w-md max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-[#262626] px-4 py-3">
              <div>
                <div className="id-label text-[#FF3333]">— Day breakdown —</div>
                <div className="font-oswald text-xl text-white uppercase tracking-tight mt-0.5" data-testid="habit-day-drill-date">
                  {new Date(drillDay.date + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}
                </div>
              </div>
              <button onClick={() => setDrillDay(null)} className="text-zinc-500 hover:text-white" aria-label="Close" data-testid="habit-day-drill-close">
                <X size={18} weight="bold" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <div className="id-label text-zinc-500 mb-2">Habits · {drillDay.done}/{drillDay.total}</div>
                {(data.habits || []).length === 0 ? (
                  <div className="text-xs text-zinc-500 italic">No habits configured.</div>
                ) : (
                  <ul className="space-y-1.5">
                    {(data.habits || []).map((h) => {
                      const done = !!(drillDay.completed && drillDay.completed[h.id]);
                      return (
                        <li key={h.id} className="flex items-center gap-2 text-sm font-manrope">
                          {done ? (
                            <CheckCircle size={14} weight="fill" className="text-[#00CC66]" />
                          ) : (
                            <MinusCircle size={14} weight="fill" className="text-zinc-700" />
                          )}
                          <span className={done ? "text-white" : "text-zinc-500 line-through"}>{h.label || h.name}</span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              {drillDay.sleep_hours != null && (
                <div className="border border-[#262626] px-3 py-2.5 flex items-center gap-2">
                  <Moon size={14} weight="fill" className={drillDay.sleep_hours < (data.sleep_minimum_hours || 6) ? "text-[#FF3333]" : "text-indigo-400"} />
                  <div className="text-sm text-white font-manrope">
                    <span className="font-bold">{drillDay.sleep_hours}h</span> sleep
                    {drillDay.sleep_hours < (data.sleep_minimum_hours || 6) && <span className="text-[#FF3333] ml-2 text-xs">(below {data.sleep_minimum_hours}h target)</span>}
                  </div>
                </div>
              )}
              {drillDay.done === 0 && drillDay.sleep_hours == null && (
                <div className="text-xs text-zinc-500 italic">Nothing logged this day.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {err && <div className="mt-3 border border-[#FF3333] bg-[#FF3333]/10 px-3 py-2 text-xs text-[#FF3333]">{err}</div>}
    </div>
  );
}

